/**
 * Query转换优化工具
 * 使用统一配置和现有的 createMetaInfo 方法
 */

import { E_QUERY_ITEM_CONDITION, IQueryItem } from '@patsnap/synapse_common_interface'
import type { IFieldGroup, IFieldGroupFieldsItem, IFieldGroupMeta } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { useFinderComp } from '@pharmsnap/pharmsnap-web/composable/useFinderComp'
import type { IBAcTag } from '@pharmsnap/shared/types'
import { DrugFinderExpertKey, getLast30daysRange, getLast3MonthsRange, getLast7daysRange, getOneYearRange } from '@pharmsnap/shared/utils'
import { createMetaInfo } from '../../../utils/advancedToExpertTransform'
import { ALL_TAG_ID, getFieldConfig, getFieldQueryField } from '../config/fieldConfig'

// 时间范围配置
const TIME_RANGE_MAP: Record<string, { from: number; to: number }> = {
  '1-w': getLast7daysRange(),
  '1-M': getLast30daysRange(),
  '3-M': getLast3MonthsRange(),
  '1-y': getOneYearRange(),
}

export type IQueryItemWithMeta = IQueryItem & { _meta?: IFieldGroupMeta }

/**
 * 检查字段是否有有效值
 */
export function hasValidValue(fieldData: { tags?: IBAcTag[]; selectedValues?: IBAcTag[] }): boolean {
  if (fieldData.tags && fieldData.tags.length > 0) return true
  if (fieldData.selectedValues && fieldData.selectedValues.length > 0) {
    return fieldData.selectedValues.some((v) => v.id !== ALL_TAG_ID)
  }
  return false
}

/**
 * 创建字段查询项
 * @param fieldKey - 字段key
 * @param fieldData - 字段数据
 * @param group - 字段组
 * @param groupIndex - 组索引
 */
export function createFieldQuery(fieldKey: DrugFinderExpertKey, fieldData: IFieldGroupFieldsItem, group: IFieldGroup): IQueryItemWithMeta | null {
  const config = getFieldConfig(fieldKey)
  const {
    action: { convert2QueryItem },
  } = useFinderComp()

  // 跳过不需要生成Query的字段
  if (config?.skipInQuery) return null

  // 处理tags类型
  if (fieldData.tags && fieldData.tags.length > 0) {
    const queryItem = convert2QueryItem({
      dataType: 'drug',
      type: config?.valueType || 'text',
      items: fieldData.tags,
      fields: fieldData.fields || [getFieldQueryField(fieldKey)],
      condition: E_QUERY_ITEM_CONDITION.ANY,
    })
    return {
      ...queryItem,
      _meta: createMetaInfo(group, fieldKey, parseInt(group.id), group.logic),
    }
  }

  // 处理selectedValues类型
  if (fieldData.selectedValues && fieldData.selectedValues.length > 0) {
    const validValues = fieldData.selectedValues.filter((v) => v.id !== ALL_TAG_ID)
    if (validValues.length === 0) return null

    const queryItem = convert2QueryItem({
      dataType: 'drug',
      type: config?.valueType || 'text',
      items: validValues,
      fields: fieldData.fields || [getFieldQueryField(fieldKey)],
      condition: E_QUERY_ITEM_CONDITION.ANY,
    })
    return {
      ...queryItem,
      _meta: createMetaInfo(group, fieldKey, parseInt(group.id), group.logic),
    }
  }

  return null
}

/**
 * 创建里程碑Query (特殊处理)
 */
export function createMilestoneQuery(group: IFieldGroup): IQueryItemWithMeta | null {
  const milestoneItems = group.fields.milestoneItems?.selectedValues as IBAcTag[] | undefined
  const milestoneDate = group.fields.milestoneDate?.selectedValues as IBAcTag[] | undefined

  if (!milestoneItems?.length || !milestoneDate?.length) return null

  const validMilestoneItems = milestoneItems.filter((v) => v.id !== ALL_TAG_ID)
  if (validMilestoneItems.length === 0) return null

  return {
    type: 'field',
    fields: validMilestoneItems.map((v) => v.id),
    condition: E_QUERY_ITEM_CONDITION.ANY,
    value: milestoneDate.map((date) => {
      const timeRange = TIME_RANGE_MAP[date.id]
      const [from, to] = date.id?.split('-')
      return {
        type: 'range',
        display_name_cn: date.display_name_cn || date.id,
        display_name_en: date.display_name_en || date.id,
        id: date.id,
        value: date.id,
        from: timeRange?.from || Number(from),
        to: timeRange?.to || Number(to),
      }
    }),
    _meta: createMetaInfo(group, 'milestoneDate', parseInt(group.id), group.logic),
  }
}

/**
 * 处理组合字段组
 */
export function processCombinedGroup(group: IFieldGroup): IQueryItemWithMeta[] {
  const queries: IQueryItemWithMeta[] = []

  // 普通组合字段处理
  Object.entries(group.fields).forEach(([fieldKey, fieldData]) => {
    if (!fieldData || (!fieldData.tags?.length && !fieldData.selectedValues?.length)) {
      return
    }
    const fieldQuery = createFieldQuery(fieldKey as DrugFinderExpertKey, fieldData, group)
    if (fieldQuery) queries.push(fieldQuery)
  })

  // 里程碑特殊处理
  if (group.property === 'milestone') {
    const milestoneQuery = createMilestoneQuery(group)
    if (milestoneQuery) queries.push(milestoneQuery)
  }

  return queries
}

/**
 * 处理简单字段组
 */
export function processSimpleGroup(
  group: IFieldGroup,
  groupIndex: number
): Array<{
  query: IQueryItemWithMeta
  logic: 'AND' | 'OR' | 'NOT'
  originalIndex: number
  originalGroup: IFieldGroup
}> {
  const queries: Array<{
    query: IQueryItemWithMeta
    logic: 'AND' | 'OR' | 'NOT'
    originalIndex: number
    originalGroup: IFieldGroup
  }> = []

  Object.entries(group.fields).forEach(([fieldKey, fieldData]) => {
    if (!hasValidValue(fieldData)) return

    const query = createFieldQuery(fieldKey as DrugFinderExpertKey, fieldData, group)
    if (query) {
      queries.push({
        query,
        logic: groupIndex === 0 ? 'AND' : group.logic,
        originalIndex: groupIndex,
        originalGroup: group,
      })
    }
  })

  return queries
}

/**
 * 构建Query层级结构
 */
export function buildQueryHierarchy(
  queries: Array<{
    query: IQueryItemWithMeta
    logic: 'AND' | 'OR' | 'NOT'
  }>
): IQueryItemWithMeta {
  if (queries.length === 0) return { type: 'group', must: [] }

  let result: IQueryItemWithMeta | null = null
  for (const item of queries) {
    let currentQuery = item.query
    if (item.logic === 'NOT') {
      // 处理NOT逻辑
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore 这里需要处理NOT逻辑
      currentQuery = {
        ...currentQuery,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore 这里需要处理NOT逻辑
        condition: E_QUERY_ITEM_CONDITION.NONE,
      }
    }
    if (result === null) {
      result = currentQuery
    } else if (item.logic === 'OR') {
      // OR逻辑
      result = {
        type: 'group',
        any: [result, currentQuery],
      }
    } else if (item.logic === 'NOT') {
      // 处理NOT逻辑
      result = {
        type: 'group',
        must: [result, currentQuery],
      }
    } else {
      // AND逻辑
      if (
        result.type === 'group' &&
        result.must &&
        !result.must.find((item) => 'condition' in item && item.condition === E_QUERY_ITEM_CONDITION.NONE)
      ) {
        result.must.push(currentQuery)
      } else {
        result = {
          type: 'group',
          must: [result, currentQuery],
        }
      }
    }
  }

  // 如果结果不是group结构，包装成group
  if (result && result.type !== 'group') {
    result = {
      type: 'group' as const,
      must: [result],
    }
  }

  return result || { type: 'group', must: [] }
}
